import React from 'react';
import { Loader2, AlertTriangle, Trash2, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * Confirmation dialog for deleting a tenant user from the super-admin panel.
 *
 * The backend enforces the rule (no operational footprint), this UI just
 * previews it — showing a per-signal breakdown so the operator understands
 * *why* a user can or cannot be deleted before pulling the trigger.
 *
 * `activity` shape (from GET .../users/{id}/activity):
 *   { email, role, activity: {assigned_reports, messages_written,
 *                             attachments_uploaded, case_notes, total},
 *     can_delete, cogniseg_managed }
 */
export const UserDeleteDialog = ({
  open, onOpenChange, targetUser, activity, error, loading, onConfirm,
}) => {
  const canDelete = !!activity?.can_delete && !loading;
  const signals = activity?.activity || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-slate-50 border-slate-200 text-slate-900" data-testid="user-delete-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900 flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" />
            Eliminar utilizador
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Vai eliminar <span className="font-medium text-slate-700">{targetUser?.email}</span>.
            Esta operação é permanente e mantida no registo de auditoria.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" className="bg-red-50 border-red-200">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <AlertDescription className="text-red-700">{error}</AlertDescription>
          </Alert>
        )}

        {loading && !activity && (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> A verificar atividade...
          </div>
        )}

        {activity && (
          <div className="space-y-3">
            <div className={`rounded-lg border p-3 ${canDelete ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
              <div className="flex items-start gap-2">
                {canDelete
                  ? <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                  : <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />}
                <div className="text-sm">
                  <p className={`font-medium ${canDelete ? 'text-emerald-800' : 'text-amber-800'}`}>
                    {canDelete
                      ? 'Sem atividade — pode ser eliminado'
                      : signals.total > 0
                        ? `${signals.total} registo(s) associados`
                        : 'Não pode ser eliminado (regra do sistema)'}
                  </p>
                  {!canDelete && signals.total === 0 && (
                    <p className="text-amber-800/80 text-xs mt-1">
                      É o único administrador ativo do cliente ou um utilizador gerido pela plataforma.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <ul className="text-xs space-y-1 text-slate-600" data-testid="user-delete-activity-list">
              <li className="flex justify-between"><span>Denúncias atribuídas</span>       <span className="font-mono">{signals.assigned_reports ?? 0}</span></li>
              <li className="flex justify-between"><span>Mensagens do caso escritas</span> <span className="font-mono">{signals.messages_written ?? 0}</span></li>
              <li className="flex justify-between"><span>Anexos submetidos</span>          <span className="font-mono">{signals.attachments_uploaded ?? 0}</span></li>
              <li className="flex justify-between"><span>Notas de caso</span>              <span className="font-mono">{signals.case_notes ?? 0}</span></li>
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-300 text-slate-600 hover:bg-slate-100">
            Cancelar
          </Button>
          <Button
            variant="destructive"
            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            onClick={onConfirm}
            disabled={!canDelete}
            data-testid="user-delete-confirm-btn"
          >
            {loading && activity ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Eliminar definitivamente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
